(() => {
  if (customElements.get('luveon-discount')) return;
  const normalize = value => String(value || '').trim().toLowerCase();
  const reduction = (cart, code) => {
    let amount = 0;
    let found = false;
    const add = (application, value) => {
      if (application?.type !== 'discount_code' || normalize(application.code || application.title) !== normalize(code)) return;
      if (!Number.isFinite(Number(value))) return;
      found = true;
      amount += Number(value);
    };
    (cart.cart_level_discount_applications || []).forEach(d => add(d, d.total_allocated_amount));
    (cart.items || []).forEach(i => (i.line_level_discount_allocations || []).forEach(d => add(d.discount_application, d.amount)));
    return found ? amount : null;
  };
  const codes = cart => {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.filter(d => d && typeof d.code === 'string')
        .map(d => ({ code: d.code, applicable: d.applicable === true }));
    }
    const found = new Map();
    const add = (d, amount) => {
      if (d?.type !== 'discount_code' || !d.title) return;
      const key = normalize(d.title);
      found.set(key, { code: d.title, applicable: (found.get(key)?.applicable || Number(amount) > 0) });
    };
    (cart.cart_level_discount_applications || []).forEach(d => add(d, d.total_allocated_amount));
    (cart.items || []).forEach(i => (i.line_level_discount_allocations || []).forEach(d => add(d.discount_application, d.amount)));
    return [...found.values()];
  };
  class LuveonDiscount extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.input = this.querySelector('[data-lv-input]');
      this.status = this.querySelector('[data-lv-status]');
      this.applied = this.querySelector('[data-lv-applied]');
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        if (button.hasAttribute('data-lv-apply')) this.change();
        if (button.dataset.removeCode) this.change(button.dataset.removeCode);
      }, { signal: this.abort.signal });
      this.addEventListener('keydown', event => {
        if (event.target === this.input && event.key === 'Enter') {
          event.preventDefault(); event.stopPropagation(); this.change();
        }
      }, { signal: this.abort.signal });
      this.sync();
    }
    disconnectedCallback() { this.abort?.abort(); }
    async request(discount) {
      const root = window.Shopify?.routes?.root || '/';
      const options = { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
      if (discount !== undefined) {
        options.method = 'POST';
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({ discount });
      }
      const response = await fetch(root + (discount === undefined ? 'cart.js' : 'cart/update.js'), options);
      if (!response.ok) throw new Error('Cart request failed');
      const cart = await response.json();
      if (!Number.isFinite(cart.total_price) || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
      return cart;
    }
    message(text, error = false) {
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.classList.toggle('lv-discount-error', error);
      this.input.setAttribute('aria-invalid', error ? 'true' : 'false');
    }
    paint(cart) {
      const active = codes(cart).filter(d => d.applicable);
      this.applied.replaceChildren();
      for (const entry of active) {
        const line = document.createElement('div');
        const label = document.createElement('span');
        const amount = reduction(cart, entry.code);
        const saving = amount === null ? ' · discount calculated at checkout' : ' · −' + new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency', currency: cart.currency || this.dataset.currency || 'USD'
        }).format(amount / 100);
        label.textContent = 'Discount code applied: ' + entry.code + saving;
        const remove = document.createElement('button');
        remove.type = 'button'; remove.dataset.removeCode = entry.code;
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', 'Remove le code ' + entry.code);
        line.append(label, remove); this.applied.append(line);
      }
      const total = this.closest('cart-drawer')?.querySelector('.totals__total-value');
      if (total) total.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: cart.currency || this.dataset.currency || 'USD'
      }).format(cart.total_price / 100);
      this.cart = cart;
      return active;
    }
    busy(value) {
      this.pending = value;
      this.setAttribute('aria-busy', String(value));
      if (value) {
        this.controls = [...this.closest('cart-drawer').querySelectorAll('button, input, textarea')]
          .filter(el => !el.classList.contains('drawer__close'))
          .map(el => ({ el, disabled: el.disabled }));
        this.controls.forEach(({ el }) => { el.disabled = true; });
      } else {
        this.controls?.forEach(({ el, disabled }) => { el.disabled = disabled; });
        this.querySelectorAll('button').forEach(el => { el.disabled = false; });
        this.input.disabled = false;
      }
    }
    async sync() {
      this.busy(true);
      try { this.paint(await this.request()); }
      catch { this.message('Unable to check your cart right now. Please try again.', true); }
      finally { this.busy(false); }
    }
    async change(removeCode) {
      if (this.pending) return;
      const entered = this.input.value.trim();
      if (!removeCode && (!entered || entered.includes(','))) {
        this.message('Saisissez un seul code promo.', true); return;
      }
      this.busy(true); this.message('Checking…');
      try {
        const before = await this.request();
        const previous = codes(before).filter(d => d.applicable).map(d => d.code);
        const wanted = removeCode
          ? previous.filter(code => normalize(code) !== normalize(removeCode))
          : [...previous.filter(code => normalize(code) !== normalize(entered)), entered];
        let cart = await this.request(wanted.join(','));
        let active = this.paint(cart);
        if (removeCode) {
          if (codes(cart).some(d => normalize(d.code) === normalize(removeCode))) throw new Error('Removal unconfirmed');
          this.message('Discount code removed.'); this.input.value = '';
        } else if (active.some(d => normalize(d.code) === normalize(entered))) {
          this.message('');
        } else {
          // Remove the rejected candidate and preserve the previously applied codes.
          cart = await this.request(previous.join(',')); this.paint(cart);
          this.message('Discount code “' + entered + '” does not work: it is invalid or does not apply to your cart.', true);
        }
      } catch {
        try { this.paint(await this.request()); } catch { /* Keep the last confirmed total. */ }
        this.message('Unable to confirm the change. Please try again.', true);
      } finally { this.busy(false); }
    }
  }
  customElements.define('luveon-discount', LuveonDiscount);
})();

